import React from "react";
import {
  CheckCircle2,
  Clock,
  XCircle,
  Package,
  Truck,
  CreditCard,
  RotateCcw,
} from "lucide-react";

export interface StatusConfig {
  label: string;
  translationKey: string;
  badgeClass: string;
  icon: React.ReactElement;
  progressStep: number; // 0=failed/canceled, 1=pending, 2=paid/processing, 3=shipped, 4=delivered/completed
}

/**
 * Get status configuration for individual Order status (OrderStatuses)
 * pending | paid | processing | shipped | delivered | canceled | refunded | failed
 */
export const getStatusConfig = (
  statusRaw: string | object,
  t: (key: string) => string,
): StatusConfig => {
  const statusString =
    typeof statusRaw === "object" && statusRaw !== null
      ? (statusRaw as Record<string, unknown>)?.status
        ? String((statusRaw as Record<string, unknown>).status)
        : String(statusRaw)
      : String(statusRaw);

  const status = statusString.toLowerCase();

  switch (status) {
    case "pending":
      return {
        label: t("orders.detail.status.pending"),
        translationKey: "orders.detail.status.pending",
        badgeClass:
          "bg-amber-50 text-amber-600 border border-amber-200 dark:bg-amber-900/20 dark:text-amber-400 dark:border-amber-800",
        icon: <CreditCard className="h-3.5 w-3.5" />,
        progressStep: 1,
      };

    case "paid":
      return {
        label: t("orders.detail.status.paid"),
        translationKey: "orders.detail.status.paid",
        badgeClass:
          "bg-blue-50 text-blue-600 border border-blue-200 dark:bg-blue-900/20 dark:text-blue-400 dark:border-blue-800",
        icon: <CheckCircle2 className="h-3.5 w-3.5" />,
        progressStep: 2,
      };

    case "processing":
      return {
        label: t("orders.detail.status.processing"),
        translationKey: "orders.detail.status.processing",
        badgeClass:
          "bg-amber-50 text-amber-600 border border-amber-200 dark:bg-amber-900/20 dark:text-amber-400 dark:border-amber-800",
        icon: <Clock className="h-3.5 w-3.5" />,
        progressStep: 2,
      };

    case "shipped":
      return {
        label: t("orders.detail.status.shipped"),
        translationKey: "orders.detail.status.shipped",
        badgeClass:
          "bg-indigo-50 text-indigo-600 border border-indigo-200 dark:bg-indigo-900/20 dark:text-indigo-400 dark:border-indigo-800",
        icon: <Truck className="h-3.5 w-3.5" />,
        progressStep: 3,
      };

    case "delivered":
      return {
        label: t("orders.detail.status.delivered"),
        translationKey: "orders.detail.status.delivered",
        badgeClass:
          "bg-green-50 text-green-600 border border-green-200 dark:bg-green-900/20 dark:text-green-400 dark:border-green-800",
        icon: <CheckCircle2 className="h-3.5 w-3.5" />,
        progressStep: 4,
      };

    case "canceled":
    case "cancelled":
      return {
        label: t("orders.detail.status.canceled"),
        translationKey: "orders.detail.status.canceled",
        badgeClass:
          "bg-red-50 text-red-600 border border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800",
        icon: <XCircle className="h-3.5 w-3.5" />,
        progressStep: 0,
      };

    case "refunded":
      return {
        label: t("orders.detail.status.refunded"),
        translationKey: "orders.detail.status.refunded",
        badgeClass:
          "bg-orange-50 text-orange-600 border border-orange-200 dark:bg-orange-900/20 dark:text-orange-400 dark:border-orange-800",
        icon: <RotateCcw className="h-3.5 w-3.5" />,
        progressStep: 0,
      };

    case "failed":
      return {
        label: t("orders.detail.status.failed"),
        translationKey: "orders.detail.status.failed",
        badgeClass:
          "bg-red-50 text-red-600 border border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800",
        icon: <XCircle className="h-3.5 w-3.5" />,
        progressStep: 0,
      };

    default:
      return {
        label: t("orders.status.unknown"),
        translationKey: "orders.status.unknown",
        badgeClass:
          "bg-gray-100 text-gray-600 border border-gray-200 dark:bg-gray-900/40 dark:text-gray-300 dark:border-gray-700",
        icon: <Package className="h-3.5 w-3.5" />,
        progressStep: 0,
      };
  }
};

/**
 * Get status badge config for OrderGroup list (OrderGroupStatuses)
 * active | completed | canceled | refunded
 */
export const getOrderGroupStatusConfig = (
  statusRaw: string,
  t: (key: string) => string,
) => {
  const status = String(statusRaw).toLowerCase();

  switch (status) {
    case "active":
      return {
        bg: "bg-amber-50 dark:bg-amber-900/20",
        text: "text-amber-600 dark:text-amber-400",
        icon: <Clock className="w-full h-full" />,
        label: t("orders.status.active"),
      };

    case "completed":
      return {
        bg: "bg-green-50 dark:bg-green-900/20",
        text: "text-green-600 dark:text-green-400",
        icon: <CheckCircle2 className="w-full h-full" />,
        label: t("orders.status.completed"),
      };

    case "canceled":
    case "cancelled":
      return {
        bg: "bg-red-50 dark:bg-red-900/20",
        text: "text-red-600 dark:text-red-400",
        icon: <XCircle className="w-full h-full" />,
        label: t("orders.status.canceled"),
      };

    case "refunded":
      return {
        bg: "bg-orange-50 dark:bg-orange-900/20",
        text: "text-orange-600 dark:text-orange-400",
        icon: <RotateCcw className="w-full h-full" />,
        label: t("orders.status.refunded"),
      };

    default:
      return {
        bg: "bg-amber-50 dark:bg-amber-900/20",
        text: "text-amber-600 dark:text-amber-400",
        icon: <Clock className="w-full h-full" />,
        label: t("orders.status.active"),
      };
  }
};
